import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch, buildQueryString } from "./client";
import type {
  GenerateRequest,
  GenerateResponse,
  IterateRequest,
  ProjectKind,
  Prompt,
  PromptHistoryParams,
} from "@/types/prompt";

export const promptKeys = {
  all: ["prompts"] as const,
  history: (params: PromptHistoryParams) =>
    ["prompts", "history", params] as const,
  detail: (id: string) => ["prompts", id] as const,
  sessions: (params: PromptHistoryParams) =>
    ["prompts", "sessions", params] as const,
  sessionChain: (rootId: string) =>
    ["prompts", "sessions", rootId] as const,
  root: (leafId: string) => ["prompts", leafId, "root"] as const,
  sessionContext: (rootId: string) =>
    ["prompts", "sessions", rootId, "context"] as const,
};

/** One entry in the session context ledger (Context tab). */
export interface ContextEntry {
  key: string;
  type: "color" | "text" | "font" | "url" | "number" | "file";
  value: string;
  /** Who wrote it: the user (authoritative), the generating model, or the
   *  server's brand extractor ("site"). */
  source: "user" | "model" | "site";
  updated_at?: string;
}

export interface SessionContextResponse {
  root_id: string;
  entries: ContextEntry[];
}

export function useSessionContextQuery(sessionId: string | null | undefined) {
  return useQuery({
    queryKey: promptKeys.sessionContext(sessionId ?? ""),
    queryFn: () =>
      apiFetch<SessionContextResponse>(`/prompts/sessions/${sessionId}/context`),
    enabled: !!sessionId,
  });
}

/** Replace the session's context ledger with the tab's edited copy. */
export function useSaveContextMutation(sessionId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entries: ContextEntry[]) =>
      apiFetch<SessionContextResponse>(
        `/prompts/sessions/${sessionId}/context`,
        { method: "PUT", body: JSON.stringify({ entries }) },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(
        promptKeys.sessionContext(sessionId ?? ""),
        data,
      );
    },
  });
}

/**
 * Fetch the SESSION ROOT for any prompt id. Walks parent_id upward
 * server-side and returns the original prompt that started the chain.
 * Used so TemplateDetail can show the *initial* prompt that created
 * the graphic, instead of whatever iteration the template was last
 * saved against.
 */
export function useRootPromptQuery(leafId: string | null | undefined) {
  return useQuery({
    queryKey: promptKeys.root(leafId ?? ""),
    queryFn: () => apiFetch<Prompt>(`/prompts/${leafId}/root`),
    enabled: !!leafId,
    // Root prompt text is essentially immutable — once a chain exists,
    // its root never changes. Long stale time keeps unnecessary refetches
    // off the wire when the user navigates around.
    staleTime: 5 * 60 * 1000,
  });
}

export function usePromptHistoryQuery(params: PromptHistoryParams = {}) {
  return useQuery({
    queryKey: promptKeys.history(params),
    queryFn: () =>
      apiFetch<Prompt[]>(
        `/prompts/history${buildQueryString(params as Record<string, string | number | boolean | undefined>)}`,
      ),
  });
}

export function usePromptByIdQuery(id: string) {
  return useQuery({
    queryKey: promptKeys.detail(id),
    queryFn: () => apiFetch<Prompt>(`/prompts/${id}`),
    enabled: !!id,
  });
}

export function useSessionsQuery(params: PromptHistoryParams = {}) {
  return useQuery({
    queryKey: promptKeys.sessions(params),
    queryFn: () =>
      apiFetch<Prompt[]>(
        `/prompts/sessions${buildQueryString(params as Record<string, string | number | boolean | undefined>)}`,
      ),
  });
}

export function useSessionChainQuery(rootId: string) {
  return useQuery({
    queryKey: promptKeys.sessionChain(rootId),
    queryFn: () => apiFetch<Prompt[]>(`/prompts/sessions/${rootId}`),
    enabled: !!rootId,
  });
}

export function useGenerateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GenerateRequest) =>
      apiFetch<GenerateResponse>("/prompts/generate", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptKeys.all });
    },
  });
}

export interface CheckpointRequest {
  tsx: string;
  fields: unknown[];
  parent_id?: string | null;
  label?: string;
  /** Only honored when creating a session ROOT (no parent_id); parented
   *  checkpoints inherit the root's kind. */
  project_kind?: ProjectKind;
}

export interface CheckpointResponse {
  prompt_id: string;
  session_id: string;
}

/**
 * Persist a manual editor save as a version in the session chain — the
 * workspace's "lock in the current state" action. No model call.
 */
export function useCheckpointMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CheckpointRequest) =>
      apiFetch<CheckpointResponse>("/prompts/checkpoint", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptKeys.all });
    },
  });
}

export function useIterateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IterateRequest) =>
      apiFetch<GenerateResponse>("/prompts/iterate", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptKeys.all });
    },
  });
}
